/** @jsx jsx */
import type { SerializedStyles } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useSelector } from 'react-redux';

import { Suggestion } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import { COACH_RECOMMENDED_ACTION_CLICK } from 'bundles/ai-coach-platform/components/patterns/chat/constants';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import type { SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';
import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';

const styles = {
  root: css`
    /* overrides CDS 320px max-width on buttons */
    max-width: 100%;
  `,
};

type Props = {
  action: string;
  sendMessage: SendMessageFunc;
  actionMessage: string;
  actionNotClicked: boolean;
  isRecommendedAction: boolean;
  isRecommendedActionsLabelRendered: boolean;
  onRecommendedActionClick?: () => void;
  /**
   * Optional caller-supplied styles applied on top of the default button styles.
   * Temporary prop for the Two-Panel XDP experiment (MERCH-2963). Please consult the platform team before using it in your project.
   */
  customStyles?: SerializedStyles;
  variant?: 'default' | 'cds-labs';
};

export const RecommendedActionButton = ({
  actionMessage,
  action,
  sendMessage,
  actionNotClicked,
  isRecommendedAction,
  isRecommendedActionsLabelRendered,
  onRecommendedActionClick,
  customStyles,
  variant = 'default',
}: Props): React.ReactElement | null => {
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;
  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
  }));

  const isDisabled = isMessageLoading || isMessageStreaming;

  const handleClick = () => {
    const isSetClickTimestamp = isRecommendedAction && actionNotClicked && isRecommendedActionsLabelRendered;
    if (isSetClickTimestamp) {
      const currentDate = new Date();
      sessionStorageEx.setItem(COACH_RECOMMENDED_ACTION_CLICK, currentDate.toISOString(), (value) => value);
    }
    onRecommendedActionClick?.();
    sendMessage(actionMessage, action);

    if (trackingContext) {
      track('interact_coach', {
        coachInteractionType: 'click_action',
        coach: {
          mode: trackingContext.mode,
          ...(trackingContext.location && { location: trackingContext.location }),
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
          },
        }),
        coachAction: action,
      });

      // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
      track('click_coach_element', {
        clickElementType: 'action',
        coach: {
          mode: trackingContext.mode,
          ...(trackingContext.location && { location: trackingContext.location }),
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
          },
        }),
        coachAction: action,
      });
    }
  };

  if (variant === 'cds-labs') {
    return (
      <Suggestion disabled={isDisabled} onClick={handleClick}>
        {actionMessage}
      </Suggestion>
    );
  }

  return (
    <CoachButton
      css={[styles.root, customStyles]}
      disabled={isDisabled}
      variant="secondary"
      size="small"
      onClick={handleClick}
    >
      {actionMessage}
    </CoachButton>
  );
};
